import { Footer, FooterLink, FooterLinkGroup } from "flowbite-react";
import { useState, useEffect } from "react";
import type { FC, PropsWithChildren } from "react";
import { usePageTitle } from "../hooks/usePageTitle";
import Navbar from "../components/navbar";
import Sidebar from "../components/sidebar";
import { MdFacebook } from "react-icons/md";
import { FaDribbble, FaGithub, FaInstagram, FaTwitter } from "react-icons/fa";

interface NavbarSidebarLayoutProps {
  isFooter?: boolean;
  pageTitle: string;
}

const NavbarSidebarLayout: FC<PropsWithChildren<NavbarSidebarLayoutProps>> =
  function ({ children, isFooter = true, pageTitle }) {
    usePageTitle(pageTitle);

    // Initialize sidebar state based on localStorage and screen size
    const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
      // Check if we have a saved preference
      const savedState = localStorage.getItem("sidebarOpen");
      // Get current screen width
      const screenWidth = window.innerWidth;

      if (savedState !== null) {
        // Use saved preference if available
        return savedState === "true";
      } else {
        // Default based on screen size:
        // - Mobile (< 768px): closed by default
        // - Tablet and Desktop (≥ 768px): open by default
        return screenWidth >= 768;
      }
    });

    // Update sidebar state on screen resize
    useEffect(() => {
      const handleResize = () => {
        const screenWidth = window.innerWidth;
        // Only set default state if there's no saved preference
        if (localStorage.getItem("sidebarOpen") === null) {
          setIsSidebarOpen(screenWidth >= 768);
        }
      };

      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Save sidebar state to localStorage when it changes
    useEffect(() => {
      localStorage.setItem("sidebarOpen", isSidebarOpen.toString());
    }, [isSidebarOpen]);

    // Handle toggle sidebar
    const handleToggleSidebar = () => {
      setIsSidebarOpen(!isSidebarOpen);
    };

    return (
      <>
        <Navbar onToggleSidebar={handleToggleSidebar} />
        <div className="relative flex items-start pt-16">
          {/* Overlay */}
          <button
            onClick={() => setIsSidebarOpen(false)}
            type="button"
            aria-label="Close sidebar"
            className={`fixed inset-0 z-10 bg-gray-900/50 transition-opacity lg:hidden ${
              isSidebarOpen ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
          />
          <div
            className={`fixed left-0 top-0 z-20 h-full w-64 overflow-hidden border-r border-gray-200 bg-white pt-16 shadow-lg transition-transform dark:border-gray-700 dark:bg-gray-800 lg:shadow-none ${
              isSidebarOpen ? "translate-x-0" : "-translate-x-full"
            }`}
          >
            <Sidebar />
          </div>
          <MainContent isFooter={isFooter} isSidebarOpen={isSidebarOpen}>
            {children}
          </MainContent>
        </div>
      </>
    );
  };

// Omit pageTitle from NavbarSidebarLayoutProps since MainContent doesn't need it
interface MainContentProps extends Omit<NavbarSidebarLayoutProps, "pageTitle"> {
  isSidebarOpen?: boolean;
}

const MainContent: FC<PropsWithChildren<MainContentProps>> = function ({
  children,
  isFooter,
  isSidebarOpen,
}) {
  return (
    <main
      className={`relative h-full w-full overflow-y-auto bg-gray-50 px-0.5 py-6 transition-all dark:bg-gray-900 lg:px-2 ${
        isSidebarOpen ? "lg:ml-64" : "lg:ml-0"
      }`}
    >
      {children}
      {isFooter && (
        <div className="mx-4 mt-4">
          <MainContentFooter />
        </div>
      )}
    </main>
  );
};

const MainContentFooter: FC = function () {
  return (
    <>
      <Footer container>
        <div className="flex w-full flex-col gap-y-6 lg:flex-row lg:justify-between lg:gap-y-0">
          <FooterLinkGroup>
            <FooterLink href="#" className="mr-3 mb-3 lg:mb-0">
              Allgemeine Geschäftsbedingungen
            </FooterLink>
            <FooterLink href="#" className="mr-3 mb-3 lg:mb-0">
              Datenschutzerklärung
            </FooterLink>
            <FooterLink href="#" className="mr-3">
              Lizenzierung
            </FooterLink>
            <FooterLink href="#" className="mr-3">
              Cookie-Richtlinie
            </FooterLink>
            <FooterLink href="#">Kontakt</FooterLink>
          </FooterLinkGroup>
          <FooterLinkGroup>
            <div className="flex gap-x-1">
              <FooterLink
                href="#"
                className="hover:[&>*]:text-black dark:hover:[&>*]:text-gray-300"
              >
                <MdFacebook className="text-lg" />
              </FooterLink>
              <FooterLink
                href="#"
                className="hover:[&>*]:text-black dark:hover:[&>*]:text-gray-300"
              >
                <FaInstagram className="text-lg" />
              </FooterLink>
              <FooterLink
                href="#"
                className="hover:[&>*]:text-black dark:hover:[&>*]:text-gray-300"
              >
                <FaTwitter className="text-lg" />
              </FooterLink>
              <FooterLink
                href="#"
                className="hover:[&>*]:text-black dark:hover:[&>*]:text-gray-300"
              >
                <FaGithub className="text-lg" />
              </FooterLink>
              <FooterLink
                href="#"
                className="hover:[&>*]:text-black dark:hover:[&>*]:text-gray-300"
              >
                <FaDribbble className="text-lg" />
              </FooterLink>
            </div>
          </FooterLinkGroup>
        </div>
      </Footer>
      <p className="my-8 text-center text-sm text-gray-500 dark:text-gray-300">
        &copy; 2025 - D2D Boom.
      </p>
      <p className="my-8 text-center text-sm text-gray-500 dark:text-gray-300">
        Build with 🤍 by{" "}
        <a
          href="https://leibnizdata.com"
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          LeibnizData
        </a>
        .
      </p>
    </>
  );
};

export default NavbarSidebarLayout;
